/* eslint-disable react-hooks/exhaustive-deps, react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from "jwt-decode";
import { useNavigate, Link, useLocation } from 'react-router-dom';
import Pagination from "react-js-pagination";
import Navbar from "./Navbar";
import Header from "./Header";
import baseUrl from "./BaseUrl";
import UserSegmentToggle from "./common/UserSegmentToggle";
import { useUserSegment, watchFilterValue } from "../context/UserSegmentContext";
import ExportButton from "./active-users/ExportButton";
import DateRangeBar, { fmt, presetRange } from "./active-users/DateRangeBar";
import EmptyState from "./active-users/states/EmptyState";
import ErrorState from "./active-users/states/ErrorState";
import TableSkeleton from "./active-users/states/TableSkeleton";
import ActivityLogSummaryCards from "./activity-logs/ActivityLogSummaryCards";
import ActivityLogTrendChart from "./activity-logs/ActivityLogTrendChart";
import TopNamesPanel from "./activity-logs/TopNamesPanel";
import { dateFormatter, formatLastActive } from "../utils/utils";
import { readSavedFilters, usePersistFilters } from "../hooks/filterPersistence";
import "./active-users/active-users.css";
import "./activity-logs/activity-logs.css";

const LIMIT = 20;
const STATE_KEY = "eventLogsState";
const EXPORT_COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'platform', label: 'Platform' },
  { key: 'mobile_app_version', label: 'Mobile Version' },
  { key: 'watch_app_version', label: 'Watch Version' },
  { key: 'event_name', label: 'Event Name' },
  { key: 'created_at', label: 'Datetime' },
  { key: 'last_active', label: 'Last Active (UTC)' },
];

const EventLogs = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const pinnedUserId = location.state?.user_id || null;       // optional server-side pin
  const { segment } = useUserSegment();                        // feature 023 (watch default)
  const userSegment = watchFilterValue(segment) || 'all';

  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [rows, setRows] = useState([]);
  const [totalItem, setTotalItem] = useState(0);
  const [uniqueUserCount, setUniqueUserCount] = useState(0);
  const [subscribedUserCount, setSubscribedUserCount] = useState(0);
  const [totalUserBase, setTotalUserBase] = useState(0);
  const [summary, setSummary] = useState({ logs: {}, users: {} });
  const [trend, setTrend] = useState([]);
  const [categorical, setCategorical] = useState({ logs: 0, topNames: [] });
  const [page, setPage] = useState(1);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [displayFromDate, setDisplayFromDate] = useState(null);
  const [displayToDate, setDisplayToDate] = useState(null);
  const [preset, setPreset] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [subscriptionFilter, setSubscriptionFilter] = useState('');
  const [order, setOrder] = useState('DESC');                 // created_at direction (whitelisted)

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(async (config) => {
    if (expire * 1000 < new Date().getTime()) {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      config.headers.Authorization = `Bearer ${res.data.accessToken}`;
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    }
    return config;
  }, (err) => Promise.reject(err));

  useEffect(() => {
    // Restore the view on return from a drill-down (Back), else default Last 30.
    const saved = readSavedFilters(STATE_KEY);
    if (saved) {
      setFromDate(saved.fromDate || ''); setToDate(saved.toDate || '');
      setDisplayFromDate(saved.fromDate ? new Date(saved.fromDate) : null);
      setDisplayToDate(saved.toDate ? new Date(saved.toDate) : null);
      setPreset(saved.preset || null);
      setSearchValue(saved.searchValue || '');
      setSubscriptionFilter(saved.subscriptionFilter || '');
      setOrder(saved.order || 'DESC');
      setPage(saved.page || 1);
    } else {
      const { fromStr, toStr } = presetRange('last30', new Date());   // default Last 30 days
      setFromDate(fromStr); setToDate(toStr);
      setDisplayFromDate(new Date(fromStr)); setDisplayToDate(new Date(toStr));
      setPreset('last30');
    }
    refreshToken();
  }, []);

  const refreshToken = async () => {
    try {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    } catch (err) {
      if (err.response) { navigate('/'); window.location.reload(); }
    }
  };

  useEffect(() => { if (token) loadAll(page, fromDate, toDate, searchValue, order, subscriptionFilter); }, [token]);

  const didMountSegment = useRef(false);
  useEffect(() => {
    if (!didMountSegment.current) { didMountSegment.current = true; return; }
    if (!token || pinnedUserId) return;   // pin overrides segment → toggle inert
    setPage(1);
    loadAll(1, fromDate, toDate, searchValue, order, subscriptionFilter);
  }, [segment]);

  // Snapshot filters so the exact view is restored on Back (remount).
  usePersistFilters(STATE_KEY, { fromDate, toDate, preset, searchValue, subscriptionFilter, order, page });

  const auth = () => ({ headers: { Authorization: `Bearer ${token}` } });
  const pin = pinnedUserId ? `&user_id=${pinnedUserId}` : '';
  const listQs = (p, f, t, s, o, sub) =>
    `page=${p}&limit=${LIMIT}&from_date=${f}&to_date=${t}&search=${encodeURIComponent(s)}&order=${o}&subscription_status=${sub}&user_segment=${userSegment}${pin}`;
  const rangeQs = (f, t, s, sub) =>
    `from_date=${f}&to_date=${t}&search=${encodeURIComponent(s)}&subscription_status=${sub}&user_segment=${userSegment}${pin}`;

  const applyList = (d) => {
    setRows(d.data || []);
    setTotalItem(d.totalItem || 0);
    setUniqueUserCount(d.uniqueUserCount || 0);
    setSubscribedUserCount(d.subscribedUserCount || 0);
    setTotalUserBase(d.totalUserCount || 0);
  };
  const handleErr = (err) => { setError(err?.response?.status === 401 ? 'auth' : 'load'); };

  const getList = async (p, f, t, s, o, sub) => {
    setLoading(true); setError('');
    try {
      const res = await axiosJWT.get(`${baseUrl}/admin/api/event-logs?${listQs(p, f, t, s, o, sub)}`, auth());
      applyList(res.data);
    } catch (err) { handleErr(err); } finally { setLoading(false); }
  };

  const loadAll = async (p, f, t, s, o, sub) => {
    setLoading(true); setError('');
    const range = rangeQs(f, t, s, sub);
    try {
      const [list, sum, tr, cat] = await Promise.all([
        axiosJWT.get(`${baseUrl}/admin/api/event-logs?${listQs(p, f, t, s, o, sub)}`, auth()),
        axiosJWT.get(`${baseUrl}/admin/api/event-logs-summary?${range}`, auth()),
        axiosJWT.get(`${baseUrl}/admin/api/event-logs-trend?${range}`, auth()),
        axiosJWT.get(`${baseUrl}/admin/api/event-logs-categorical?${range}`, auth()),
      ]);
      applyList(list.data);
      setSummary({ logs: sum?.data?.logs || {}, users: sum?.data?.users || {} });
      setTrend(Array.isArray(tr?.data?.data) ? tr.data.data : []);
      setCategorical({ logs: cat?.data?.logs || 0, topNames: cat?.data?.topNames || [] });
    } catch (err) { handleErr(err); } finally { setLoading(false); }
  };

  const handlePageChange = (p) => { setPage(p); getList(p, fromDate, toDate, searchValue, order, subscriptionFilter); };
  const handleFromChange = (date) => { setPreset('custom'); setDisplayFromDate(date); setFromDate(date ? fmt(date) : ''); };
  const handleToChange = (date) => { setPreset('custom'); setDisplayToDate(date); setToDate(date ? fmt(date) : ''); };
  const handlePreset = (key, fromStr, toStr) => {
    setPreset(key); setFromDate(fromStr); setToDate(toStr);
    setDisplayFromDate(fromStr ? new Date(fromStr) : null); setDisplayToDate(toStr ? new Date(toStr) : null);
    setPage(1); loadAll(1, fromStr, toStr, searchValue, order, subscriptionFilter);
  };
  const handleSearch = () => { setPage(1); loadAll(1, fromDate, toDate, searchValue, order, subscriptionFilter); };
  const clearFilter = () => {
    setFromDate(''); setToDate(''); setDisplayFromDate(null); setDisplayToDate(null);
    setSearchValue(''); setSubscriptionFilter(''); setPreset(null); setPage(1);
    loadAll(1, '', '', '', order, '');
  };
  const toggleSort = () => {
    const next = order === 'DESC' ? 'ASC' : 'DESC';
    setOrder(next); setPage(1);
    loadAll(1, fromDate, toDate, searchValue, next, subscriptionFilter);
  };

  const fetchExport = async () => {
    const res = await axiosJWT.get(
      `${baseUrl}/admin/api/event-logs-export?${rangeQs(fromDate, toDate, searchValue, subscriptionFilter)}&order=${order}`, auth());
    const data = res.data?.data || [];
    return data.map((r) => ({
      ...r,
      created_at: r.created_at ? dateFormatter(r.created_at) : '',
      last_active: r.last_active ? formatLastActive(r.last_active) : '',
    }));
  };

  const hasRange = !!(fromDate || toDate || searchValue || subscriptionFilter);
  const dmy = (s) => { if (!s) return ''; const [y, m, d] = s.split('-'); return `${d}/${m}/${y}`; };
  const logsVal = summary.logs?.current ?? totalItem;
  const usersVal = summary.users?.current ?? uniqueUserCount;

  return (
    <div className="panelbox">
      <Navbar />
      <div className="right-panel">
        <Header name="Pauser" />
        <div className="content-page">
          <div className="au-head">
            <span className="au-breadcrumb">Activity › <b>Event logs</b></span>
            <span className="au-route">/event-logs</span>
            <span className="au-spacer" />
            {!pinnedUserId && <UserSegmentToggle />}
            <span className="au-back"><Link to="/dashboard">← Back</Link></span>
          </div>
          <h1 className="au-title">Event Logs</h1>

          {error === 'auth' ? (
            <ErrorState title="Your session expired" message="Please sign in again to continue." onRetry={() => navigate('/')} />
          ) : error === 'load' ? (
            <ErrorState message="We couldn’t load the event logs. Please try again." onRetry={() => loadAll(page, fromDate, toDate, searchValue, order, subscriptionFilter)} />
          ) : (
            <>
              <ActivityLogSummaryCards
                logs={logsVal}
                users={usersVal}
                totalUserBase={totalUserBase}
                subscribedUserCount={subscribedUserCount}
                logsDelta={summary.logs?.delta}
                usersDelta={summary.users?.delta}
              />

              <ActivityLogTrendChart data={trend} title="Event logs per day" seriesName="Event logs" />

              <TopNamesPanel title="Top event names" items={categorical.topNames} logs={categorical.logs} />

              <DateRangeBar
                leading={(
                  <>
                    <input className="au-input" type="text" placeholder="Search name, email, version, or event" value={searchValue} onChange={(e) => setSearchValue(e.target.value)} aria-label="Search event logs" />
                    <select className="au-select" value={subscriptionFilter} onChange={(e) => setSubscriptionFilter(e.target.value)} aria-label="Subscription filter">
                      <option value="">All Subscriptions</option>
                      <option value="1">Active</option>
                      <option value="2">Cancel</option>
                      <option value="4">Expired</option>
                    </select>
                  </>
                )}
                preset={preset} displayFrom={displayFromDate} displayTo={displayToDate} hasRange={hasRange}
                onPreset={handlePreset} onFromChange={handleFromChange} onToChange={handleToChange}
                onSearch={handleSearch} onClear={clearFilter}
              />
              <p className="au-range-note" data-testid="al-range-note">
                The date range filters logs by their <b>record date (UTC)</b> — the value shown per row as “Datetime”.{' '}
                {(fromDate || toDate)
                  ? <>Showing <b>{dmy(fromDate) || '…'}</b> – <b>{dmy(toDate) || '…'}</b>.</>
                  : 'Showing all logs (no date filter).'}
              </p>

              {loading ? (
                <div className="au-panel"><TableSkeleton rows={8} /></div>
              ) : rows.length === 0 ? (
                <EmptyState title="No event logs in this range" hasFilters={hasRange} onClear={clearFilter}
                  message={hasRange ? 'No logs match the current filters or segment.' : 'There are no event logs to show for this view yet.'} />
              ) : (
                <div className="au-panel">
                  <div className="au-panel__head">
                    <h4 className="au-panel__title">Event log records</h4>
                    <span className="au-spacer" />
                    <ExportButton columns={EXPORT_COLUMNS} fetchRows={fetchExport} filename="event-logs.csv" />
                  </div>
                  <div className="au-table-wrap">
                    <table className="au-table al-table" data-testid="al-table">
                      <thead>
                        <tr>
                          <th>No</th><th>Name</th><th>Email</th><th>Platform</th><th>Mobile Version</th><th>Watch Version</th><th>Event Name</th>
                          <th className="au-th-sort" onClick={toggleSort} role="button" tabIndex={0}>
                            Datetime {order === 'DESC' ? '▼' : '▲'}
                          </th>
                          <th>Last Active (UTC)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r, i) => (
                          <tr key={r.id}>
                            <td data-label="No">{(page - 1) * LIMIT + i + 1}</td>
                            <td data-label="Name" className="au-ellipsis">{r.name}</td>
                            <td data-label="Email" className="au-ellipsis">{r.email}</td>
                            <td data-label="Platform">{r.platform}</td>
                            <td data-label="Mobile Version">{r.mobile_app_version}</td>
                            <td data-label="Watch Version">{r.watch_app_version}</td>
                            <td data-label="Event Name" className="au-ellipsis">{r.event_name}</td>
                            <td data-label="Datetime">{r.created_at ? dateFormatter(r.created_at) : ''}</td>
                            <td data-label="Last Active (UTC)">{r.last_active ? formatLastActive(r.last_active) : ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {totalItem > LIMIT && (
                    <Pagination activePage={page} itemsCountPerPage={LIMIT} totalItemsCount={totalItem} pageRangeDisplayed={5} onChange={handlePageChange} />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventLogs;
